import { supabase } from '../lib/supabase';

/**
 * Hybrid Credit Service for CareerLift
 * Handles both monthly subscription credits and purchased credits with FEFO consumption
 */
class HybridCreditService {
  /**
   * Get user's credit balance (both monthly and purchased)
   */
  static async getCreditBalance(userId) {
    try {
      const { data, error } = await supabase
        .from('assessment_credits')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        // Initialize user's credit account
        const { data: newAccount, error: initError } = await supabase
          .from('assessment_credits')
          .insert({
            user_id: userId,
            monthly_credits_available: 0,
            purchased_credits_available: 0
          })
          .select()
          .single();

        if (initError) throw initError;
        return { success: true, data: newAccount };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching credit balance:', error);
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Get detailed credit breakdown with expiry info
   */
  static async getCreditBreakdown(userId) {
    try {
      // Try to use the SQL function first
      try {
        const { data: result, error: funcError } = await supabase.rpc('get_user_credit_breakdown', {
          p_user_id: userId
        });

        if (!funcError && result) {
          return { success: true, data: result };
        }
      } catch (funcErr) {
        console.log('SQL function not available, falling back to manual query');
      }

      // Fallback to manual queries
      // Get current snapshot
      const { data: snapshot, error: snapshotError } = await supabase
        .from('assessment_credits')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (snapshotError && snapshotError.code !== 'PGRST116') {
        throw snapshotError;
      }

      // Get active credit lots with expiry info
      const { data: lots, error: lotsError } = await supabase
        .from('credit_lots')
        .select('*')
        .eq('user_id', userId)
        .gt('expires_at', new Date().toISOString())
        .gt('quantity_remaining', 0)
        .order('expires_at', { ascending: true });

      if (lotsError) throw lotsError;

      // Separate monthly and purchased lots
      const monthlyLots = lots?.filter(lot => lot.source === 'monthly_grant') || [];
      const purchasedLots = lots?.filter(lot => lot.source === 'purchase') || [];

      // Find soonest expiry
      const soonestExpiry = lots?.length > 0 ? lots[0].expires_at : null;

      return {
        success: true,
        data: {
          snapshot: snapshot || {
            monthly_credits_available: 0,
            purchased_credits_available: 0,
            updated_at: new Date().toISOString()
          },
          monthlyLots,
          purchasedLots,
          soonestExpiry,
          totalAvailable: (snapshot?.monthly_credits_available || 0) + (snapshot?.purchased_credits_available || 0)
        }
      };
    } catch (error) {
      console.error('Error fetching credit breakdown:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user can take an assessment
   */
  static async canTakeAssessment(userId, isPremium = false) {
    try {
      console.log('canTakeAssessment called with:', { userId, isPremium });
      
      // Try to use the SQL function first, but don't rely on it
      try {
        const { data: result, error: funcError } = await supabase.rpc('can_user_take_assessment', {
          p_user_id: userId,
          p_is_premium: isPremium
        });

        console.log('SQL function result:', { result, funcError });

        if (!funcError && result && result.success !== undefined) {
          console.log('Using SQL function result:', result);
          return { 
            success: result.success, 
            canTake: result.can_take, 
            reason: result.reason 
          };
        }
      } catch (funcErr) {
        console.log('SQL function error, falling back to manual query:', funcErr);
      }

      // Fallback to manual logic - ALWAYS use this for now
      console.log('Using manual credit check logic');
      
      // Check credit balance for both free and premium users
      const { data: balance, error: balanceError } = await this.getCreditBalance(userId);
      if (balanceError) {
        console.error('Error getting credit balance:', balanceError);
        // If we can't check credits, assume they can take an assessment
        return { success: true, canTake: true, reason: 'free_first' };
      }

      const totalCredits = (balance.monthly_credits_available || 0) + (balance.purchased_credits_available || 0);
      console.log('Credit balance for user:', { 
        userId, 
        isPremium, 
        monthlyCredits: balance.monthly_credits_available, 
        purchasedCredits: balance.purchased_credits_available, 
        totalCredits 
      });
      
      if (totalCredits > 0) {
        const result = { success: true, canTake: true, reason: isPremium ? 'credits' : 'free_first' };
        console.log('Returning eligibility result:', result);
        return result;
      } else {
        const result = { success: true, canTake: false, reason: 'no_credits' };
        console.log('Returning eligibility result:', result);
        return result;
      }
    } catch (error) {
      console.error('Error checking assessment eligibility:', error);
      return { success: false, canTake: false, error: error.message };
    }
  }

  /**
   * Consume a credit for an assessment (FEFO - First Expiring, First Out)
   */
  static async consumeCreditForAssessment(userId, assessmentId) {
    try {
      // Find usable lots (not expired, quantity_remaining > 0) ordered by expires_at ASC
      const { data: lots, error: lotsError } = await supabase
        .from('credit_lots')
        .select('*')
        .eq('user_id', userId)
        .gt('expires_at', new Date().toISOString())
        .gt('quantity_remaining', 0)
        .order('expires_at', { ascending: true })
        .limit(1);

      if (lotsError) throw lotsError;

      if (!lots || lots.length === 0) {
        return { success: false, error: 'NO_CREDITS' };
      }

      const lot = lots[0];

      // Consume 1 credit from the earliest expiring lot
      const { data: updatedLot, error: updateError } = await supabase
        .from('credit_lots')
        .update({ quantity_remaining: lot.quantity_remaining - 1 })
        .eq('id', lot.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Add ledger entry
      const { error: ledgerError } = await supabase
        .from('credit_ledger')
        .insert({
          user_id: userId,
          delta: -1,
          reason: 'assessment_use',
          lot_id: lot.id,
          details: { assessment_id }
        });

      if (ledgerError) throw ledgerError;

      // Recompute snapshot
      const { error: recomputeError } = await supabase.rpc('recompute_credit_snapshot', {
        p_user_id: userId
      });

      if (recomputeError) throw recomputeError;

      return { success: true, data: { lot_id: lot.id, remaining: updatedLot.quantity_remaining } };
    } catch (error) {
      console.error('Error consuming credit:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get credit history/ledger
   */
  static async getCreditHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('credit_ledger')
        .select(`
          *,
          lot:credit_lots(source, quantity, granted_at, expires_at)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching credit history:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Grant monthly credits (called by Stripe webhook)
   */
  static async grantMonthlyCredits(userId, quantity = 3) {
    try {
      // Calculate end of month
      const now = new Date();
      const eom = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
      eom.setUTCSeconds(eom.getUTCSeconds() - 1); // last second of current month

      // Create monthly lot
      const { data: lot, error: lotError } = await supabase
        .from('credit_lots')
        .insert({
          user_id: userId,
          source: 'monthly_grant',
          quantity: quantity,
          quantity_remaining: quantity,
          expires_at: eom.toISOString(),
          metadata: { type: 'monthly_subscription' }
        })
        .select()
        .single();

      if (lotError) throw lotError;

      // Add ledger entry
      const { error: ledgerError } = await supabase
        .from('credit_ledger')
        .insert({
          user_id: userId,
          delta: quantity,
          reason: 'monthly_grant',
          lot_id: lot.id,
          details: { monthly_grant: true }
        });

      if (ledgerError) throw ledgerError;

      // Recompute snapshot
      const { error: recomputeError } = await supabase.rpc('recompute_credit_snapshot', {
        p_user_id: userId
      });

      if (recomputeError) throw recomputeError;

      return { success: true, data: lot };
    } catch (error) {
      console.error('Error granting monthly credits:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Grant purchased credits (called by Stripe webhook)
   */
  static async grantPurchasedCredits(userId, quantity, metadata = {}) {
    try {
      // Set expiry to 3 months from now
      const expiresAt = new Date();
      expiresAt.setMonth(expiresAt.getMonth() + 3);

      // Create purchase lot
      const { data: lot, error: lotError } = await supabase
        .from('credit_lots')
        .insert({
          user_id: userId,
          source: 'purchase',
          quantity: quantity,
          quantity_remaining: quantity,
          expires_at: expiresAt.toISOString(),
          metadata: { ...metadata, type: 'purchased' }
        })
        .select()
        .single();

      if (lotError) throw lotError;

      // Add ledger entry
      const { error: ledgerError } = await supabase
        .from('credit_ledger')
        .insert({
          user_id: userId,
          delta: quantity,
          reason: 'purchase',
          lot_id: lot.id,
          details: { purchase: true, ...metadata }
        });

      if (ledgerError) throw ledgerError;

      // Recompute snapshot
      const { error: recomputeError } = await supabase.rpc('recompute_credit_snapshot', {
        p_user_id: userId
      });

      if (recomputeError) throw recomputeError;

      return { success: true, data: lot };
    } catch (error) {
      console.error('Error granting purchased credits:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add credits (e.g., from purchase or monthly grant)
   */
  static async addCredits(userId, source, quantity, expiresAt, metadata = {}) {
    try {
      const { data, error } = await supabase.from('credit_lots').insert({
        user_id: userId,
        source,
        quantity,
        quantity_remaining: quantity,
        expires_at: expiresAt,
        metadata
      }).select().single();

      if (error) throw error;

      // Recompute the credit snapshot
      const { error: recomputeError } = await supabase.rpc('recompute_credit_snapshot', { 
        p_user_id: userId 
      });

      if (recomputeError) throw recomputeError;

      return { success: true, data };
    } catch (error) {
      console.error('Error adding credits:', error);
      return { success: false, error: error.message };
    }
  }
}

export default HybridCreditService;
